import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Loader2, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const cellStyle = (coverage) => {
  if (coverage >= 70) return "bg-teal-50 text-teal-800";
  if (coverage >= 50) return "bg-amber-50 text-amber-800";
  return "bg-red-50 text-red-800";
};

export default function SkillsIntelligencePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [heat, setHeat] = useState(null);
  const [selected, setSelected] = useState(null); // {department, target}
  const [detail, setDetail] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  useEffect(() => {
    api.get("/skills/heatmap").then((r) => setHeat(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    setDetail(null);
    api.get(`/skills/gaps/${encodeURIComponent(selected.department)}`, { params: { target: selected.target } })
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [selected]);

  const runBriefing = async () => {
    setBriefingLoading(true);
    try {
      const { data } = await api.post("/skills/briefing");
      setBriefing(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBriefingLoading(false);
    }
  };

  if (!heat) return <Skeleton className="h-96 w-full rounded-none" />;

  return (
    <div className="space-y-6 fade-in" data-testid="skills-intelligence-page">
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-muted-foreground max-w-2xl">
          Department coverage vs company strategic targets. Effective proficiency propagates through the
          skill adjacency graph — not keyword matching.
        </p>
        {["admin", "hr_manager"].includes(user.role) && (
          <Button onClick={runBriefing} disabled={briefingLoading} data-testid="skills-briefing-btn"
            className="rounded-none bg-teal-700 hover:bg-teal-800 text-white h-9 text-[13px] shrink-0">
            {briefingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Strategic Skills Briefing
          </Button>
        )}
      </div>

      {briefing && (
        <div className="border border-teal-600/40 bg-white p-5 fade-in" data-testid="skills-briefing-panel">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-teal-700" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-800">Strategic Skills Briefing</span>
            <span className="text-[10px] font-mono text-muted-foreground ml-auto">
              non-PII aggregate · gateway audit {briefing.audit_id.slice(0, 8)} · {briefing.model}
            </span>
          </div>
          <p className="text-[13px] leading-relaxed text-slate-800 whitespace-pre-wrap">{briefing.briefing}</p>
        </div>
      )}

      {/* Heat view */}
      <div className="bg-white border border-border overflow-x-auto" data-testid="skills-heatmap">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground font-semibold px-4 py-3 border-b border-border min-w-[160px]">
                Department
              </th>
              {heat.targets.map((t) => (
                <th key={t} className="text-left text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-semibold px-3 py-3 border-b border-l border-border min-w-[170px]">
                  {t.length > 42 ? t.slice(0, 42) + "…" : t}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {heat.departments.map((row) => (
              <tr key={row.department} className="border-b border-border last:border-b-0">
                <td className="px-4 py-3 text-[13px] font-medium text-slate-800">
                  {row.department}
                  <span className="block text-[10px] font-mono text-muted-foreground">{row.headcount} staff</span>
                </td>
                {row.targets.map((cell) => {
                  const isSel = selected?.department === row.department && selected?.target === cell.target;
                  const isOwner = heat.owners?.[cell.target] === row.department;
                  return (
                    <td key={cell.target} className="border-l border-border p-1">
                      <button
                        data-testid={`heat-cell-${row.department.replace(/[^a-z]/gi, "")}-${cell.target.slice(0, 10).replace(/[^a-z]/gi, "")}`}
                        onClick={() => setSelected({ department: row.department, target: cell.target })}
                        className={`w-full text-left px-3 py-2 transition-colors ${cellStyle(cell.coverage)} ${isSel ? "ring-1 ring-[var(--brand-primary)]" : "hover:ring-1 hover:ring-slate-300"} ${isOwner ? "ring-1 ring-teal-600/60" : ""}`}>
                        <span className="text-[15px] font-bold">{cell.coverage}%</span>
                        {isOwner && (
                          <span className="text-[8px] font-mono uppercase tracking-wider bg-[var(--brand-primary)] text-white px-1 py-0.5 ml-1.5 align-middle">
                            owner
                          </span>
                        )}
                        <span className="block text-[10px] font-mono opacity-80 truncate">
                          gaps: {cell.top_gaps.join(", ")}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drilldown */}
      {selected && (
        <div className="bg-white border border-border p-6 fade-in" data-testid="skills-gap-detail">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Gap detail</p>
          <h3 className="text-[15px] font-bold text-[var(--brand-primary)] mt-1">
            {selected.department} · {selected.target}
          </h3>
          {!detail ? (
            <Skeleton className="h-40 w-full rounded-none mt-4" />
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mt-5">
              <div className="space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Skill coverage (dept avg effective vs required 4.0)
                </p>
                {detail.skills.map((s) => (
                  <div key={s.skill} className="flex items-center gap-3">
                    <span className="w-44 text-[12px] text-slate-600 shrink-0">{s.skill}</span>
                    <div className="flex-1 h-2 bg-slate-100">
                      <div className={`h-2 ${s.coverage >= 70 ? "bg-teal-600" : s.coverage >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                        style={{ width: `${s.coverage}%` }} />
                    </div>
                    <span className="text-[11px] font-mono text-slate-500 w-14 text-right">{s.coverage}%</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
                  Closest to closing each gap
                </p>
                <div className="space-y-4">
                  {detail.top_gaps.map((skill) => (
                    <div key={skill}>
                      <p className="text-[12px] font-semibold text-slate-700 mb-1.5">{skill}</p>
                      <div className="divide-y divide-border border border-border">
                        {(detail.closest_to_close[skill] || []).slice(0, 3).map((p) => (
                          <button key={p.employee_id} onClick={() => navigate(`/employees/${p.employee_id}`)}
                            className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 text-left">
                            <span className="text-[12px] text-slate-800">{p.name}
                              <span className="text-muted-foreground ml-1.5 text-[11px]">{p.title}</span>
                            </span>
                            <span className="text-[11px] font-mono text-teal-700">eff {p.effective}/5</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
